/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErroDoUsuarioPontotel } from '@/tipos/erros/classes'
import { observabilidadeLegada, normalizarErroLegado } from '@/integracoes/src-next/observabilidade'
import { OPERACOES_ERRO } from '@next/compartilhados/observabilidade'

interface ReportadorDeErros {
  reportarErro(erro: unknown, tags?: Record<string, any>): void;
  reportarMensagem(mensagem: string): void;
}

/** Nome público legado preservado; a implementação encaminha à observabilidade nova. */
export class SentryAdapter implements ReportadorDeErros {
  reportarErro(erro: unknown, tags?: Record<string, any>): void {
    let operacao: typeof OPERACOES_ERRO[number] | undefined
    let endpointModelo: string | undefined
    try { operacao = OPERACOES_ERRO.find(valor => valor === tags?.operacao) } catch { /* Ainda registrar sem operação. */ }
    try { if (typeof tags?.endpoint_modelo === 'string') endpointModelo = tags.endpoint_modelo } catch { /* Modelo é opcional. */ }
    observabilidadeLegada.registrarErro(erro, { origem: 'legado', ...(operacao ? { operacao } : {}), ...(endpointModelo ? { endpointModelo } : {}) })
  }

  reportarMensagem(mensagem: string): void {
    observabilidadeLegada.registrarErro(new Error(mensagem), { origem: 'legado' })
  }
}

interface ErroDoServidorComData extends Error {
  data: {
    error?: string;
    detail?: string;
    description?: string;
    id?: string;
    message?: string;
  }
}

interface ErroDoServidorComBody extends Error {
  body: {
    error?: string;
    detail?: string;
    description?: string;
    id?: string;
    message?: string;
  }
}

export class FiltradorDeErros {
  private static reportadorDeErros: ReportadorDeErros = new SentryAdapter();

  private static verificarSeEhErroDoUsuario (erro: Error) {
    return erro instanceof ErroDoUsuarioPontotel
  }

  static capturarErro (erro: unknown, tags?: Record<string, any>) {
    this.filtrarErroParaDiagnostico(erro, tags)
  }

  private static extrairMensagemDoErro (erro: ErroDoServidorComData | ErroDoServidorComBody) {
    const data = (erro as ErroDoServidorComData).data
    const body = (erro as ErroDoServidorComBody).body

    // formatos encontrados ao longo do repositório e que necessitam de refatoração para padronizar
    const mensagem = (data && (data.error || data.detail || data.description || data.message)) ||
      (body && (body.error || body.detail || body.description || body.message))

    // caso não encontre nenhum dos formatos acima, retorna a mensagem padrão
    if (mensagem) {
      return mensagem
    } else {
      // caso não encontre nenhum dos formatos acima, retorna a mensagem padrão
      return ''
    }
  }

  private static ehErroComDataOuBody (erro: Error): erro is ErroDoServidorComData | ErroDoServidorComBody {
    return 'data' in erro || 'body' in erro
  }

  static enviarMensagem (mensagem: string) {
    this.reportadorDeErros.reportarMensagem(mensagem)
  }

  private static enviarErro (erro: unknown, tags?: Record<string, any>) {
    this.reportadorDeErros.reportarErro(erro, tags)
  }

  static capturarErroDandoFeedback (erro: Error, mostrarFeedbackParaUsuario: (mensagemDoErro?: string) => void, tags?: Record<string, any>) {
    if (this.verificarSeEhErroDoUsuario(erro)) {
      // Se o erro for do usuário, devemos informá-lo.
      const mensagemDoErro = this.ehErroComDataOuBody(erro) ? this.extrairMensagemDoErro(erro) : erro.message
      mostrarFeedbackParaUsuario(mensagemDoErro)
    } else {
      // Se não for do usuário, devemos informar o desenvolvedor.
      this.filtrarErroParaDiagnostico(erro, tags)
    }
  }

  private static verificarSeEhErroConhecido (erro: unknown) {
    return normalizarErroLegado(erro)?.categoria === 'cancelamento'
  }

  private static filtrarErroParaDiagnostico (erro: unknown, tags?: Record<string, any>) {
    const ehErroConhecido = this.verificarSeEhErroConhecido(erro)

    if (!ehErroConhecido) {
      this.enviarErro(erro, tags)
    }
  }
}

/**
 * Exemplo de uso
 */
/**
 * try {
 *   throw new EntradaInvalidaDoUsuarioErro("Entrada inválida.");
 * } catch (erro) {
 *   this.$filtradorDeErros.capturarErro(erro as Error);
 * }
 */

export const FiltradorDeErrosPlugin = {
  install (app: any) {
    app.config.globalProperties.$filtradorDeErros = FiltradorDeErros
  }
}
